import React from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";

import { addEditingRequest, updateEditingRequestAdmin } from "services";

import { formatDate, useGetVerificationEditingRequests } from "utils";

import { ADD_EDITING_REQUEST, GET_FIELD_VERIFICATIONS, GET_VERIFICATION_EDITING_REQUESTS, UPDATE_EDITING_REQUEST } from "consts";

import { AddEditingRequestRequestBody, CropFCPO, VerificationEditingRequest } from "models";

import { Button, Flex, Form, IconLinkButton, Modal, Option, Select, SkeletonWrapper, TagNormal, TextArea, Message as message } from "components";

type RequestType = AddEditingRequestRequestBody["requestType"];
type CropType = AddEditingRequestRequestBody["cropType"];

export const EditingRequestModal = ({
  crop,
  year,
  verificationYear,
  fieldId,
  farmId,
  editingRequests = [],
  showModal,
  onClose,
}: {
  crop?: CropFCPO | null;
  year?: number | null;
  verificationYear: number;
  fieldId: string;
  farmId: string;
  editingRequests?: VerificationEditingRequest[];
  showModal: boolean;
  onClose: () => void;
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Helper function to get verifier name from the editing request data
  const getVerifierName = (request: VerificationEditingRequest) => request.verifier?.name || "Unknown Verifier";

  // Helper function to get CSS class name for request status
  const getRequestStatusClass = (request: VerificationEditingRequest) => {
    const status = getRequestStatus(request);

    // Map status to CSS class name
    const statusClassMap: Record<string, string> = {
      Pending: "pending",
      Completed: "completed",
      "Completed & Reviewed": "completed-and-reviewed",
    };

    const statusClass = statusClassMap[status] || "pending";
    return `editing-request-status ${statusClass}`;
  };

  const { isFetching: isFetchingEditingRequests } = useGetVerificationEditingRequests({ farmId, isAdmin: true });

  const { mutate: addMutate, isPending: addIsPending } = useMutation({
    mutationKey: [ADD_EDITING_REQUEST],
    mutationFn: ({ comment, requestType, cropType }: { comment: string; requestType: RequestType; cropType: CropType }) =>
      addEditingRequest({
        fieldId,
        fieldCropId: crop?.id ?? null,
        comment,
        requestType: crop || year ? "crop" : requestType,
        cropType: cropType ?? null,
        year,
        verificationYear,
      }),
    onSuccess: () => {
      message({ icon: "gremlin", content: "Editing request created successfully" });
      // Invalidate the editing requests query to refresh the data
      queryClient.invalidateQueries({ queryKey: [GET_VERIFICATION_EDITING_REQUESTS, farmId] });
      // Also invalidate field verifications to update the editing request counts
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_VERIFICATIONS] });

      // Reset the form but keep the modal open to show the new request
      form.resetFields();
    },
  });

  const { mutate: reviewEditingRequest, isPending: reviewIsPending } = useMutation({
    mutationKey: [UPDATE_EDITING_REQUEST],
    mutationFn: (request: VerificationEditingRequest) =>
      updateEditingRequestAdmin({
        id: request.id,
        reviewedAt: moment().toISOString(),
        // If not yet completed we also want to complete it with this review
        completedAt: !request.completedAt ? moment().toISOString() : undefined,
      }),
    onSuccess: () => {
      message({ icon: "gremlin", content: "Editing request resolved" });
      // Invalidate the editing requests query to refresh the data
      queryClient.invalidateQueries({ queryKey: [GET_VERIFICATION_EDITING_REQUESTS, farmId] });
      // Also invalidate field verifications to update the editing request counts
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_VERIFICATIONS] });
    },
  });

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  // Filter editing requests based on context:
  // - If crop is provided: show crop-specific requests
  // - If no crop: show field-level requests (no fieldCropId)
  const relevantEditingRequests = crop?.id
    ? editingRequests.filter((request) => request.fieldCropId === crop.id)
    : editingRequests.filter((request) => !request.fieldCropId);

  // Sort by creation date (newest first)
  const sortedEditingRequests = relevantEditingRequests.sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf());

  const getRequestStatus = (request: VerificationEditingRequest) => {
    if (request.completedAt && request.reviewedAt) return "Completed & Reviewed";
    if (request.completedAt) return "Completed";
    return "Pending";
  };

  // Check if there are any pending or unconfirmed editing requests
  const hasPendingRequests = sortedEditingRequests.some((request) => !request.completedAt || (request.completedAt && !request.reviewedAt));

  // Custom footer with past editing requests
  const customFooter = (
    <div className="editing-request-modal-footer">
      <div className="modal-buttons">
        <Button onClick={handleClose}>Close</Button>
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={addIsPending}
          disabled={hasPendingRequests}
          title={hasPendingRequests ? "Cannot create new request while there are pending or unconfirmed requests" : ""}
        >
          Create request
        </Button>
      </div>

      {hasPendingRequests && (
        <div className="editing-request-warning">
          <span className="paragraph-xs text-disabled">Cannot create new editing request while there are uncompleted or unreviewed requests.</span>
        </div>
      )}

      {/* Past Editing Requests Section */}
      {sortedEditingRequests.length > 0 && (
        <div className="editing-requests-history">
          <h4 className="h4 margin-bottom-100">Past Editing Requests {crop ? `for ${crop.cropName}` : "for this field"}</h4>
          <div className="editing-requests-list">
            {sortedEditingRequests.map((request) => (
              <div key={request.id} className="editing-request-item">
                <div className="margin-bottom-50">
                  <span className="paragraph-xs text-disabled">{formatDate(request.createdAt.toString(), "MMM DD, YYYY HH:mm", moment.ISO_8601)}</span>
                  <div className="paragraph-xs text-disabled editing-request-verifier">Created by: {getVerifierName(request)}</div>
                </div>

                <Flex justify="left" className="margin-bottom-100">
                  <SkeletonWrapper isLoading={reviewIsPending || isFetchingEditingRequests} type="button" buttonShape="round" buttonSize="small">
                    <TagNormal className={getRequestStatusClass(request)} text={getRequestStatus(request)} />
                  </SkeletonWrapper>

                  {getRequestStatus(request) !== "Completed & Reviewed" && (
                    <IconLinkButton
                      className="margin-left-50"
                      iconName="tabler/check"
                      text="Resolve"
                      disabled={reviewIsPending || isFetchingEditingRequests}
                      onClick={() => reviewEditingRequest(request)}
                    />
                  )}
                </Flex>

                {request.comment && <div className="paragraph-s margin-bottom-50">Request: {request.comment}</div>}
                {request.userComment && (
                  <div className="editing-request-user-comment">
                    <div className="paragraph-s margin-bottom-50">User Response: {request.userComment}</div>
                  </div>
                )}
                {(request.completedAt || request.reviewedAt) && (
                  <div className="editing-request-timeline">
                    {request.completedAt && (
                      <div className="paragraph-xs text-disabled">
                        Completed: {formatDate(request.completedAt.toString(), "MMM DD, YYYY HH:mm", moment.ISO_8601)}
                      </div>
                    )}
                    {request.reviewedAt && (
                      <div className="paragraph-xs text-disabled">
                        Reviewed: {formatDate(request.reviewedAt.toString(), "MMM DD, YYYY HH:mm", moment.ISO_8601)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      showModal={showModal}
      onOk={() => form.submit()}
      onCancel={handleClose}
      okText={"Create editing request"}
      cancelText={"Cancel"}
      title={"Editing request"}
      loading={addIsPending}
      footer={customFooter}
      content={
        <>
          <p>Send a message to the farmer requesting corrections for {crop?.cropName ?? "this field"}.</p>
          <Form form={form} onFinish={addMutate} className="padding-50">
            {/* We only need to show the type select if this request is created for a field */}
            {!crop && !year && (
              //  @ts-expect-error Select not yet in ts
              <Select name="requestType" label="What needs to be edited?" className="margin-bottom-100" rules={[{ required: true }]}>
                <Option value="fieldShape" key="fieldShape">
                  Field shape
                </Option>
                <Option value="fieldSplit" key="fieldSplit">
                  Field split
                </Option>
                <Option value="fieldRedzone" key="fieldRedzone">
                  Field redzone
                </Option>
              </Select>
            )}

            {/* If the request is for a year we want to know which type of crop is missing */}
            {year && (
              //  @ts-expect-error Select not yet in ts
              <Select name="cropType" label="Which crop is missing?" className="margin-bottom-100" rules={[{ required: true }]}>
                <Option value="MAIN" key="main">
                  Main crop
                </Option>
                <Option value="COVER" key="cover">
                  Cover crop
                </Option>
                <Option value="UNDERSOWING" key="undersowing">
                  Undersowing
                </Option>
              </Select>
            )}

            <TextArea name="comment" label="Leave a comment" />
          </Form>
        </>
      }
    />
  );
};
